import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { usePublicLegalIndex } from "@/api/queries/legal";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import {
  OPEN_COOKIE_SETTINGS_EVENT,
  postConsent,
  readConsentPrefs,
  writeConsentPrefs,
} from "@/lib/consent";
import { applyMarketingConsent } from "@/lib/personalCode";
import { applyAnalyticsConsent } from "@/lib/sentry";

/**
 * GDPR + ePrivacy compliant cookie banner with category granularity.
 *
 * PR 05 of the GDPR/ISO27001/NIS2 readiness plan. Replaces the binary
 * accept / decline banner with three categories:
 *
 *   - **Essential** (locked-on, always granted) — session cookie,
 *     cart cookie, this consent choice. ePrivacy doesn't require consent
 *     for these.
 *   - **Analytics** (default off) — Sentry FE, future telemetry.
 *     Gated by ``getConsentCategory("analytics")`` in
 *     ``main.tsx::initSentry`` so a declined visitor never loads the
 *     Sentry SDK at all.
 *   - **Marketing** (default off) — pairing a visit with the outreach
 *     e-mail it came from (personal code ``?p=``, KAN-1965).
 *
 * Decisions persist to ``localStorage[dxf_cookie_consent_v2]`` (FE
 * truth for pre-render gating) AND POST to ``/api/v1/consent`` (PR 04
 * server-side ledger — authoritative for audit).
 */
/**
 * Vzhled obou rovnocenných odpovědí — „Odmítnout" i „Přijmout vše".
 *
 * Jeden řetězec pro obě tlačítka, ne dva stejné: souhlas musí jít
 * odvolat a odmítnout stejně snadno, jako jde udělit, takže se ty dvě
 * tlačítka nesmí lišit velikostí, barvou ani kontrastem. Sdílená
 * konstanta je to, co tomu brání — dvě kopie se rozejdou při první
 * úpravě vzhledu a nikdo si toho nevšimne.
 */
const CONSENT_BUTTON_CLASS =
  "rounded bg-ink px-3 py-1.5 text-sm text-bg hover:bg-ink";

export function CookieBanner(): JSX.Element | null {
  const { t } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  // ``visible`` flips on first mount when no decision has been made
  // yet. Visitor with v1 / v2 stored prefs sees nothing.
  const [visible, setVisible] = useState(false);
  // Local UI state for the category toggles. Defaults are
  // "off" (data minimisation) — operator can flip + Save, or click
  // Accept All for the all-on shortcut.
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Dokument, na který lišta odkazuje a jehož verzi zapisuje do
  // ledgeru — z publikovaných dokumentů TOHOTO shellu. Zásady cookies
  // jsou samostatný druh; kde nejsou (dnes tenant shopy, kde je
  // cookies sekce uvnitř zásad ochrany údajů), padá se na privacy.
  const legal = usePublicLegalIndex(lang);
  // ``items`` se hlídá taky, ne jen ``data``: lišta visí na KAŽDÉ
  // stránce včetně chybových, takže jeden neočekávaný tvar odpovědi by
  // shodil celý web — a cookies lišta je to poslední, co smí padat.
  const docs = legal.data?.items ?? [];
  const policyDoc =
    docs.find((d) => d.slug === "cookies") ??
    docs.find((d) => d.slug === "privacy") ??
    null;
  const policyVersion = policyDoc?.version;

  useEffect(() => {
    const prefs = readConsentPrefs();
    if (prefs === null) {
      setVisible(true);
    }
    // „Nastavení cookies" v patičce (KAN-1939): lišta vyjede znovu
    // s tím, co je dnes uložené, ať jde volba změnit i odvolat.
    const reopen = () => {
      const current = readConsentPrefs();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  const persist = (analyticsValue: boolean, marketingValue: boolean) => {
    writeConsentPrefs(
      { analytics: analyticsValue, marketing: marketingValue },
      policyVersion,
    );
    // Fire two backend writes (one per non-essential category) so
    // the ledger has explicit grant/decline rows. We don't await.
    // ``policyVersion`` je verze SKUTEČNĚ zobrazeného dokumentu —
    // dřív se posílalo natvrdo "v1", což ukazovalo na dokument, který
    // neexistoval, takže ledger dokládal souhlas s ničím.
    void postConsent("analytics", analyticsValue, fetch, policyVersion);
    void postConsent("marketing", marketingValue, fetch, policyVersion);
    // Změna souhlasu platí hned, ne až po dalším načtení stránky.
    void applyAnalyticsConsent(analyticsValue);
    applyMarketingConsent(marketingValue);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside
      role="dialog"
      aria-label={t("cookieBanner.label")}
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface/95 p-4 shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <p className="text-sm text-ink-2">
          {t("cookieBanner.body")}{" "}
          <Link
            to={`/${lang}/${policyDoc?.slug ?? "privacy"}`}
            className="underline hover:text-ink"
            data-testid="cookie-more-info"
          >
            {t("cookieBanner.moreInfo")}
          </Link>
        </p>

        <div className="grid gap-2 text-sm sm:grid-cols-3">
          <label
            className="flex items-start gap-2 rounded border border-border p-2 opacity-70"
            data-testid="cookie-category-essential"
          >
            <input
              type="checkbox"
              checked
              disabled
              className="mt-1"
              aria-label={t("cookieBanner.category.essential")}
            />
            <span>
              <span className="block font-medium">
                {t("cookieBanner.category.essential")}
              </span>
              <span className="block text-xs text-ink-3">
                {t("cookieBanner.category.essentialHint")}
              </span>
            </span>
          </label>

          <label
            className="flex items-start gap-2 rounded border border-border p-2"
            data-testid="cookie-category-analytics"
          >
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="mt-1"
              aria-label={t("cookieBanner.category.analytics")}
              data-testid="cookie-category-analytics-toggle"
            />
            <span>
              <span className="block font-medium">
                {t("cookieBanner.category.analytics")}
              </span>
              <span className="block text-xs text-ink-3">
                {t("cookieBanner.category.analyticsHint")}
              </span>
            </span>
          </label>

          <label
            className="flex items-start gap-2 rounded border border-border p-2"
            data-testid="cookie-category-marketing"
          >
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-1"
              aria-label={t("cookieBanner.category.marketing")}
              data-testid="cookie-category-marketing-toggle"
            />
            <span>
              <span className="block font-medium">
                {t("cookieBanner.category.marketing")}
              </span>
              <span className="block text-xs text-ink-3">
                {t("cookieBanner.category.marketingHint")}
              </span>
            </span>
          </label>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {/* KAN-1496 — „Odmítnout" a „Přijmout vše" sdílejí JEDEN
              řetězec tříd, a to záměrně: odmítnutí musí jít stejně
              snadno jako přijetí (ePrivacy / GDPR, čl. 7 odst. 3).
              Do teď bylo odmítnutí tlumený obrys a přijetí plná plocha,
              což je nabízení jedné odpovědi. Neroztrhávej to zpátky na
              dvě volání ``cx`` — třída je společná právě proto, aby se
              ty dvě nemohly rozejít. */}
          <button
            type="button"
            onClick={() => persist(false, false)}
            className={CONSENT_BUTTON_CLASS}
            data-testid="cookie-decline"
          >
            {t("cookieBanner.decline")}
          </button>
          <button
            type="button"
            onClick={() => persist(analytics, marketing)}
            className="rounded border border-border-strong px-3 py-1.5 text-sm text-ink hover:bg-surface-2"
            data-testid="cookie-save-prefs"
          >
            {t("cookieBanner.savePrefs")}
          </button>
          <button
            type="button"
            onClick={() => persist(true, true)}
            className={CONSENT_BUTTON_CLASS}
            data-testid="cookie-accept"
          >
            {t("cookieBanner.acceptAll")}
          </button>
        </div>
      </div>
    </aside>
  );
}
